/**
 * Shared non-component helpers for the member detail modules.
 * This file stays JSX-free so component modules can import these values without
 * adding non-component exports of their own.
 */
import { type MemberDetail as MemberDetailData, type MemberErasureStatus } from '@/api/members';
import { buildSemanticMaps } from '@/theme';
import { shortDateLabel } from '@/utils/clockTime';
import { DATE_ONLY, formatInstant } from '@/utils/instantFormat';
import { UNNAMED_MEMBER, nonEmpty } from '@/utils/personLabel';
export type LoadState =
  | { status: 'loading' }
  | { status: 'loaded'; data: MemberDetailData }
  | { status: 'error'; error: unknown };

export const PLACEHOLDER = '—';

export function memberDetailTabA11yProps(index: number) {
  return {
    id: `member-detail-tab-${index}`,
    'aria-controls': `member-detail-tabpanel-${index}`,
  };
}

export function memberDetailPanelA11yProps(index: number) {
  return {
    role: 'tabpanel' as const,
    id: `member-detail-tabpanel-${index}`,
    'aria-labelledby': `member-detail-tab-${index}`,
    tabIndex: 0,
  };
}

/**
 * Resolve the plan/tier chip colors, falling back to the past-due amber for an
 * off-list name. Mode-aware since DNKADM-0269 — pass `theme.palette.mode`; the
 * default keeps light (and every pre-0269 call site/test) byte-identical.
 */
export function tierChip(
  tier: string,
  mode: 'light' | 'dark' = 'light',
): { label: string; fg: string; bg: string } {
  const maps = buildSemanticMaps(mode);
  const key = tier.toLowerCase().replace(/\s+/g, '_') as keyof typeof maps.tierChip;
  const fallback = maps.status.past_due;
  return maps.tierChip[key] ?? { label: tier, fg: fallback.fg, bg: fallback.bg };
}

/**
 * Resolve the status chip colors/dot, falling back to the paused violet for an
 * off-list status. Mode-aware since DNKADM-0269 — pass `theme.palette.mode`; the
 * default keeps light (and every pre-0269 call site/test) byte-identical.
 */
export function statusChip(
  status: string,
  mode: 'light' | 'dark' = 'light',
): { label: string; fg: string; bg: string; dot: string } {
  const maps = buildSemanticMaps(mode);
  const key = status.toLowerCase().replace(/\s+/g, '_') as keyof typeof maps.status;
  const fallback = maps.status.paused;
  return (
    maps.status[key] ?? { label: status, fg: fallback.fg, bg: fallback.bg, dot: fallback.dot }
  );
}

/** The access-end fields a member read carries (DNKAPI-0600), list row or detail alike. */
export interface MembershipAccessEnd {
  subscriptionStatus: string | null;
  cancelScheduledAt: string | null;
  effectiveAccessEndsOn: string | null;
}

/**
 * "Active until Oct 4, 2026" for a member who is still effectively Active but whose
 * subscription is ending — canceled, or an at-period-end cancel is scheduled — and whose
 * read carries the last day of access (DNKAPI-0600 consumer contract). `null` otherwise, so
 * the caller keeps the plain status label. Display only: the chip's color and every gate
 * still key off the effective status. The date is the server's `YYYY-MM-DD`, formatted as a
 * calendar day without any time zone conversion.
 */
export function accessEndingLabel(
  effectiveStatus: string | null,
  access: MembershipAccessEnd,
): string | null {
  if (effectiveStatus?.trim().toLowerCase() !== 'active') return null;
  const ending =
    access.subscriptionStatus?.trim().toLowerCase() === 'canceled' ||
    access.cancelScheduledAt !== null;
  const endsOn = access.effectiveAccessEndsOn;
  if (!ending || endsOn === null) return null;
  return `Active until ${shortDateLabel(endsOn) ?? endsOn}`;
}

/**
 * "Payment failed · Oct 20, 2026" for the member header chip (DNKADM-0486, DNKAPI-0704): the
 * day the failure was first recorded, on the venue's calendar (null zone: the device). Just
 * "Payment failed" when the read carries no date.
 */
export function paymentFailedLabel(since: string | null, timeZone: string | null): string {
  const day = formatInstant(since, timeZone, DATE_ONLY, '');
  return day === '' ? 'Payment failed' : `Payment failed · ${day}`;
}

/**
 * The success/outcome message for each 200 erasure status (DNKADM-0203). All three mean
 * the member's dink data is gone; `blocked` is framed as done-ish in-progress (NO retry),
 * distinct from the `409` last-owner refusal handled inside the dialog.
 */
export function erasureOutcomeMessage(status: MemberErasureStatus): string {
  switch (status) {
    case 'completed':
      return 'Member data erased.';
    case 'partial':
      return 'Member data erased. A payment-processor cleanup is being retried automatically.';
    case 'blocked':
      return 'Erasure in progress — the member’s data is erased. The payment and identity cleanup will finish automatically.';
  }
}


/** Best display name from the detail payload: name -> id -> generic. */
export function membersName(data: MemberDetailData): string {
  // DNKADM-0454 V-69: never the raw contact id as a person's name.
  return nonEmpty(data.name) ?? UNNAMED_MEMBER;
}

/**
 * "Jun 10, 2026" / raw string / placeholder — never throws. The day is the one the instant
 * falls on in `timeZone` (the venue's, DNKADM-0476 F-05; null: the device).
 */
export function formatJoined(iso: string | null, timeZone: string | null): string {
  return formatInstant(iso, timeZone, DATE_ONLY, PLACEHOLDER);
}
